import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Chip,
  Fade
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Engineering as EngineeringIcon,
  Build as BuildIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Support as SupportIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/gemistruct-logo.png';
import wordmark from '../assets/gemistruct-wordmark.png';
import heroBg from '../assets/sydney-harbour.jpg';

export default function ConsultingServices() {
  const navigate = useNavigate();

  const services = [
    {
      title: 'Structural Analysis & Design',
      description: 'Comprehensive structural analysis and design for all types of buildings and structures, ensuring compliance with Australian Standards.',
      icon: <EngineeringIcon sx={{ fontSize: 40 }} />,
      features: [
        'Load analysis and calculations',
        'Member sizing and optimization',
        'Foundation design',
        'Seismic and wind load considerations',
        'Australian Standards compliance'
      ]
    },
    {
      title: 'Construction Documentation',
      description: 'Detailed construction drawings, specifications, and documentation to ensure accurate project execution.',
      icon: <DescriptionIcon sx={{ fontSize: 40 }} />,
      features: [
        'Structural drawings and details',
        'Specification writing',
        'Construction methodology',
        'Quality control procedures',
        'As-built documentation'
      ]
    },
    {
      title: 'Expert Witness Services',
      description: 'Professional expert witness services for legal proceedings and dispute resolution.',
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      features: [
        'Technical report preparation',
        'Court testimony',
        'Dispute resolution support',
        'Independent assessments',
        'Professional opinions'
      ]
    },
    {
      title: 'Specialized Engineering',
      description: 'Specialized structural engineering solutions for complex and unique projects.',
      icon: <BuildIcon sx={{ fontSize: 40 }} />,
      features: [
        'Heritage building assessments',
        'Retrofit and strengthening',
        'Temporary works design',
        'Specialized structures',
        'Innovative solutions'
      ]
    }
  ];

  const projectTypes = [
    'Oil & gas facilities',
    'Mining infrastructure',
    'Power generation facilities',
    'Warehouses and distribution centres',
    'Processing facilities',
    'Water treatment plants',
    'Heavy industrial facilities',
    'Commercial buildings'
  ];

  return (
    <>
      {/* Header */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 10,
        }} 
        elevation={0}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {/* Clickable Logo that links to Home */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => navigate('/')}
            >
              <img src={logo} alt="Gemistruct Logo" style={{ height: 48, marginRight: 16 }} />
              <img src={wordmark} alt="Gemistruct" style={{ height: 36 }} />
            </Box>
          </Box>
          
          {/* Navigation Links */}
          <Box sx={{ display: 'flex', gap: 2, mr: 3 }}>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => navigate('/consulting')}
            >
              CONSULTING SERVICES
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white',
                }
              }}
              onClick={() => navigate('/engineering-tools')}
            >
              ENGINEERING TOOLS
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => {
                navigate('/#about');
                setTimeout(() => {
                  document.getElementById('about')?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 100);
              }}
            >
              ABOUT US
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => {
                navigate('/#contact');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 100);
              }}
            >
              CONTACT
            </Button>
          </Box>
          
          <Button 
            color="primary" 
            variant="contained" 
            sx={{ 
              fontWeight: 600,
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s ease-in-out',
              }
            }}
            onClick={() => navigate('/signin')}
          >
            Sign In
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section with Background Image */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 500, md: 600 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(20, 40, 80, 0.7)',
            zIndex: 1,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, color: '#fff', textAlign: 'center' }}>
          <Fade in timeout={1200}>
            <Box>
              <Typography variant="h2" fontWeight={700} gutterBottom color="#fff" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                Structural Engineering Consulting Services
              </Typography>
              <Typography variant="h5" color="#f5f5f5" paragraph sx={{ mb: 4, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                Certified Structural Solutions for Australiaian Industries.
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Services Overview */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight={700} sx={{ mb: 6 }}>
          Our Services
        </Typography>
        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card elevation={3} sx={{ height: '100%', borderRadius: 3 }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight={600}>
                    {service.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {service.description}
                  </Typography>
                  <List dense>
                    {service.features.map((feature, featureIndex) => (
                      <ListItem key={featureIndex} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Project Types */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom fontWeight={700} sx={{ mb: 6 }}>
            Project Types We Handle
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {projectTypes.map((projectType, index) => (
              <Grid item key={index}>
                <Chip 
                  label={projectType} 
                  variant="outlined" 
                  color="primary"
                  sx={{ fontSize: '1rem', py: 1, px: 2 }}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight={700} sx={{ mb: 6 }}>
          Why Choose Our Consulting Services?
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, height: '100%' }}>
              <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
                Professional Qualifications
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <Chip label="CPEng" color="primary" variant="outlined" />
                <Chip label="RPEQ" color="primary" variant="outlined" />
                <Chip label="NER" color="primary" variant="outlined" />
              </Box>
              <Typography variant="body1" paragraph>
                Our qualifications ensure the highest standards of professional engineering practice, 
                giving you confidence in the quality and reliability of our services.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, height: '100%' }}>
              <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
                Australian Standards Expertise
              </Typography>
              <Typography variant="body1" paragraph>
                Deep understanding of Australian Standards and building codes, ensuring all designs 
                and recommendations meet regulatory requirements and best practices.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, height: '100%' }}>
              <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
                Local Experience
              </Typography>
              <Typography variant="body1" paragraph>
                Extensive experience working with Queensland and Australian construction practices, 
                local suppliers, and regulatory requirements.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, height: '100%' }}>
              <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
                Client-Focused Approach
              </Typography>
              <Typography variant="body1" paragraph>
                We work closely with clients to understand their needs, provide clear communication, 
                and deliver solutions that meet project objectives and budget constraints.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom fontWeight={700}>
            Ready to Start Your Project?
          </Typography>
          <Typography variant="h6" paragraph sx={{ opacity: 0.9, mb: 4 }}>
            Let's discuss how we can help bring your structural engineering vision to life.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              color="secondary" 
              size="large"
              onClick={() => navigate('/')}
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 600,
                padding: '12px 32px'
              }}
            >
              Contact Us
            </Button>
            <Button 
              variant="outlined" 
              color="inherit" 
              size="large"
              onClick={() => navigate('/engineering-tools')}
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 600,
                padding: '12px 32px',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Explore Engineering Tools
            </Button>
          </Box>
        </Container>
      </Box>

        {/* Footer */}
        <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', py: 4 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  {/* Company name in text instead of logo */}
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: 'white' }}>
                    Gemistruct
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: 'grey.300' }}>
                    Professional consulting and cloud-based engineering tools for Australian structural engineering industry.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                     <Chip label="CPEng" size="small" sx={{ 
                       color: 'white', 
                       borderColor: 'white', 
                       '& .MuiChip-label': { color: 'white' }
                     }} variant="outlined" />
                     <Chip label="RPEQ" size="small" sx={{ 
                       color: 'white', 
                       borderColor: 'white', 
                       '& .MuiChip-label': { color: 'white' }
                     }} variant="outlined" />
                     <Chip label="NER" size="small" sx={{ 
                       color: 'white', 
                       borderColor: 'white', 
                       '& .MuiChip-label': { color: 'white' }
                     }} variant="outlined" />
                   </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Quick Links</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button 
                    color="inherit" 
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => navigate('/')}
                  >
                    Home
                  </Button>
                  <Button 
                    color="inherit" 
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => navigate('/engineering-tools')}
                  >
                    Engineering Tools
                  </Button>
                  <Button 
                    color="inherit" 
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => navigate('/signin')}
                  >
                    Client Portal
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ borderTop: 1, borderColor: 'grey.800', pt: 3, mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'grey.100' }}>
                Copyright © {new Date().getFullYear()} GEMISTRUCT PTY LTD ABN: 80 684 710 594{' '}
                <Button 
                  color="inherit" 
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.875rem',
                    color: 'grey.100',
                    '&:hover': { color: 'white' },
                    p: 0,
                    minWidth: 'auto'
                  }}
                  onClick={() => navigate('/privacy')}
                >
                  Privacy
                </Button>
                {' '}|{' '}
                <Button 
                  color="inherit" 
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.875rem',
                    color: 'grey.100',
                    '&:hover': { color: 'white' },
                    p: 0,
                    minWidth: 'auto'
                  }}
                  onClick={() => navigate('/terms')}
                >
                  Terms & Conditions
                </Button>
              </Typography>
            </Box>
          </Container>
        </Box>
    </>
  );
}
